import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { Feather } from "@expo/vector-icons";

import { AttributeCardDisplayVariantType, AttributeCardType } from "../types";

import { toReadableText } from "@/formatting/text";
import { type AttributeValue } from "@/schemas/attribute/AttributeSet";
import { UserAttributeType } from "@/schemas/profile/UserAttributeSet";
import { Spacings } from "@/styling/spacings";

export interface AttributeCardProps {
  attributeType: AttributeCardType;
  attributeId: UserAttributeType;
  values: AttributeValue[];
  onPress: () => void;
  icon: string;
  isDisabled: boolean;
  variant: AttributeCardDisplayVariantType;
}

const MAX_DISPLAY_ITEMS = 3;

function formatSelectionText(selectionOptions: AttributeValue[]): string {
  if (selectionOptions.length === 0) return "None selected";
  const selectedTexts = selectionOptions.map(
    (option) => option.label ?? "Unknown",
  );
  if (selectedTexts.length <= MAX_DISPLAY_ITEMS) {
    return selectedTexts.join(", ");
  }
  const displayedItems = selectedTexts.slice(0, MAX_DISPLAY_ITEMS);
  return `${displayedItems.join(", ")} +${selectedTexts.length - MAX_DISPLAY_ITEMS} more`;
}

function getDisplayText(
  type: AttributeCardType,
  options: AttributeValue[],
): string {
  switch (type) {
    case AttributeCardType.TEXT:
      return options[0]?.label ? "Tap to reveal" : "Unspecified";
    case AttributeCardType.SELECTION:
      return formatSelectionText(options);
    case AttributeCardType.DATE_PICKER:
      return options[0]?.label ?? "Unspecified";
    case AttributeCardType.LOCATION:
      return options[0]?.label ?? "Unspecified";
    default:
      return "";
  }
}

function AttributeCard({
  attributeType,
  attributeId,
  values,
  onPress,
  icon,
  isDisabled,
  variant,
}: AttributeCardProps): React.JSX.Element {
  const isAccountScreen = variant === AttributeCardDisplayVariantType.ACCOUNT;

  return (
    <TouchableOpacity
      disabled={isDisabled}
      onPress={onPress}
      style={styles.container}
    >
      <View
        style={[
          styles.infoCard,
          isAccountScreen
            ? [styles.flexDirectionRow, styles.infoCardSmallBorderRadius]
            : [
                styles.flexDirectionColumn,
                styles.infoCardLargeBorderRadius,
                styles.infoCardBorder,
              ],
        ]}
      >
        <Text
          style={[styles.icon, isAccountScreen ? styles.marginRight : null]}
        >
          {icon}
        </Text>
        <View
          style={[
            styles.contentContainer,
            isAccountScreen ? null : styles.alignItemsCenter,
          ]}
        >
          <Text style={styles.cardTitle}>{toReadableText(attributeId)}</Text>
          <Text style={styles.cardValue} numberOfLines={1} ellipsizeMode="tail">
            {getDisplayText(attributeType, values)}
          </Text>
        </View>
        <View style={styles.editButton}>
          <Feather
            name="edit"
            size={14}
            color={isDisabled ? "#999" : "#007AFF"}
          />
        </View>
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  infoCard: {
    alignItems: "center",
    padding: 12,
    backgroundColor: "#f5f5f5",
    position: "relative",
  },
  infoCardSmallBorderRadius: {
    borderRadius: 8,
  },
  infoCardLargeBorderRadius: {
    borderRadius: 20,
  },
  infoCardBorder: {
    borderWidth: 0.5,
    borderStyle: "solid",
    borderColor: "rgba(0, 0, 0, 0.15)",
  },
  flexDirectionRow: {
    flexDirection: "row",
  },
  flexDirectionColumn: {
    flexDirection: "column",
  },
  marginRight: {
    marginRight: Spacings.s3,
  },
  icon: {
    fontSize: 30,
    fontWeight: "bold",
  },
  contentContainer: {
    flex: 1,
  },
  alignItemsCenter: {
    alignItems: "center",
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: "bold",
  },
  cardValue: {
    fontSize: 14,
    color: "#666",
  },
  editButton: {
    position: "absolute",
    top: 5,
    right: 5,
    padding: 5,
  },
});

export default AttributeCard;
